<template lang="pug">
.columns
  AppTableColumn(
    v-slot="{ row }"
    header="Member"
    header-classes="vertical-top"
    name="member"
    cell-classes="max-width"
    :sort-by="buildSortOptions('member')"
  )
    InsightsMemberNameCell(
      :avatar-url="row.user.avatar_url"
      :name="row.user.name"
      :deactivated="row.user.deactivated"
      @select="switchMember(row.user)"
    )

  AppTableColumn(
    v-slot="{ row }"
    header="Remote time"
    header-classes="time-column time-column-remote vertical-top"
    name="tracked_remote"
    :sort-by="buildSortOptions('tracked_remote')"
  )
    .d-flex.align-items-center
      .insights-leaderboard-widget-time
        template(v-if="row.time_remote")
          | {{ $filters.formatDuration(row.time_remote, false) }}
        template(v-else)
          | {{ noDataCopy }}
  AppTableColumn(
    v-slot="{ row }"
    header="Office time"
    header-classes="time-column time-column-on-site vertical-top"
    name="tracked_on_site"
    :sort-by="buildSortOptions('tracked_on_site')"
  )
    .d-flex.align-items-center
      .insights-leaderboard-widget-time
        template(v-if="row.time_on_site")
          | {{ $filters.formatDuration(row.time_on_site, false) }}
        template(v-else)
          | {{ noDataCopy }}

  AppTableColumn(
    v-slot="{ row }"
    header="Remote Activity"
    header-classes="time-column time-column-remote vertical-top"
    name="activity_remote"
    :sort-by="buildSortOptions('activity_remote')"
  )
    .d-flex.align-items-center
      InsightsActivityScore.mr-10(
        :score="row.activity_remote"
        size="small"
      )
  AppTableColumn(
    v-slot="{ row }"
    header="Office Activity"
    header-classes="time-column time-column-on-site vertical-top"
    name="activity_on_site"
    :sort-by="buildSortOptions('activity_on_site')"
  )
    .d-flex.align-items-center
      InsightsActivityScore.mr-10(
        :score="row.activity_on_site"
        size="small"
      )

  AppTableColumn(
    v-slot="{ row }"
    header="Remote idle time"
    header-classes="time-column time-column-remote vertical-top"
    name="idle_time_remote"
    :sort-by="buildSortOptions('idle_time_remote')"
  )
    .d-flex.align-items-center
      InsightsActivityScore.mr-10(
        :score="row.idle_time_remote"
        size="small"
        :upper-threshold="30"
        :lower-threshold="15"
        :higher-is-positive="false"
      )
  AppTableColumn(
    v-slot="{ row }"
    header="Office idle time"
    header-classes="time-column time-column-on-site vertical-top"
    name="idle_time_on_site"
    :sort-by="buildSortOptions('idle_time_on_site')"
  )
    .d-flex.align-items-center
      InsightsActivityScore.mr-10(
        :score="row.idle_time_on_site"
        size="small"
        :upper-threshold="50"
        :lower-threshold="15"
        :higher-is-positive="false"
      )
  AppTableColumn(
    v-slot="{ row }"
    header="Remote Core Work"
    header-classes="time-column time-column-remote vertical-top"
    name="core_time_remote"
    :sort-by="buildSortOptions('core_time_remote')"
  )
    .d-flex.align-items-center
      InsightsProficiencyBadge.p-15.mr-10(
        :score="row.core_time_remote"
        size="medium"
      )
  AppTableColumn(
    v-slot="{ row }"
    header="Office Core Work"
    header-classes="time-column time-column-on-site vertical-top"
    name="core_time_on_site"
    :sort-by="buildSortOptions('core_time_on_site')"
  )
    .d-flex.align-items-center
      InsightsProficiencyBadge.p-15.mr-10(
        :score="row.core_time_on_site"
        size="medium"
      )
  AppTableColumn(
    v-slot="{ row }"
    header="Location"
    header-classes="progress-bar-column vertical-top"
    name="location_days"
    :sort-by="buildSortOptions('location_days')"
  )
    AppProgressBar(
      :key="`${row.user.id}-${locationRemoteDays(row)}-${locationOfficeDays(row)}`"
      :tracked="locationRemoteDays(row)"
      :total="locationTotalDays(row)"
      :bar-color="remoteBarColor(row)"
      :bar-background="officeBarColor(row)"
    )
    .d-flex.align-items-center.justify-content-between
      span {{ locationRemoteDays(row) }} {{ pluralize(locationRemoteDays(row), 'day') }}
      span {{ locationOfficeDays(row) }} {{ pluralize(locationOfficeDays(row), 'day') }}
</template>

<script>
import InsightsActivityScore from './InsightsActivityScore.vue';
import InsightsProficiencyBadge from './InsightsProficiencyBadge.vue';
import InsightsMemberNameCell from './InsightsMemberNameCell.vue';
import AppProgressBar from '~/components/AppProgressBar.vue';
import { InsightsFilters, InsightsTracking } from '~/mixins';
import { INSIGHTS } from '~/app_constants';
import { pluralize } from '~/utils/string_utils';

export default {
  name: 'InsightsMemberOverviewColumns',
  components: {
    InsightsActivityScore,
    InsightsProficiencyBadge,
    InsightsMemberNameCell,
    AppProgressBar,
  },
  mixins: [InsightsFilters, InsightsTracking],
  props: {
    data: {
      type: Array,
      required: true,
    },
    showMetrics: {
      type: Boolean,
    },
    sortOptions: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    tooltipOptions: Object.freeze({ placement: 'top' }),
    memberRemovedTooltip: 'This member has been removed from the organization',
    noDataCopy: INSIGHTS.empty_states.no_data.no_data_symbol,
  }),
  computed: {
    remoteComputedColor() {
      return getComputedStyle(document.documentElement).getPropertyValue('--remote-color').trim();
    },
    officeComputedColor() {
      return getComputedStyle(document.documentElement).getPropertyValue('--office-color').trim();
    },
  },
  methods: {
    pluralize,
    locationRemoteDays(row) {
      return row.location_days?.remote ?? 0;
    },
    locationOfficeDays(row) {
      return row.location_days?.on_site ?? 0;
    },
    locationTotalDays(row) {
      return this.locationRemoteDays(row) + this.locationOfficeDays(row);
    },
    remoteBarColor(row) {
      if (this.locationTotalDays(row) === 0) {
        return;
      }

      return this.remoteComputedColor;
    },
    officeBarColor(row) {
      if (this.locationTotalDays(row) === 0) {
        return;
      }

      return this.officeComputedColor;
    },
    switchMember(user) {
      const string = `${user.id}_user_id`;

      this.setPreviousView({ previousView: this.selectedFilter, currentView: string });
      this.setSelectedFilter(string);
      this.setMembersFilter(string);
    },
    buildSortOptions(field) {
      return {
        field,
        direction: this.sortOptions.field === field ? this.sortOptions.direction : null,
      };
    },
    sort(options) {
      this.$emit('sort', options);
    },
  },
};
</script>
